import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  LineChart, Line, BarChart, Bar, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, ReferenceLine,
} from "recharts";
import { Wind, CheckCircle2, AlertTriangle, AlertOctagon, Thermometer, Droplets, Sparkles, Gauge, WifiOff, Plus } from "lucide-react";
import { api } from "../../lib/api";
import { useSiteContext } from "../../components/layout/Layout";
import { useAuth } from "../../context/AuthContext";

// Live indoor air quality from the site's IAQ sensors (Asset Registries → IAQ Sensors,
// asset_type "iaq_sensor"). Readings, status and limits come from GET /iaq/sensors/live
// (backend/iaq.py); the trend from the same telemetry history the other pages use.
// Until 2026-10-22 this page was a mock-up: six hardcoded zones, a random CO₂ trend, fixed alerts.

const REFRESH_MS = 30000;
const METRICS = [
  { key: "co2", label: "CO₂", kpi: "Avg CO₂", icon: Gauge, digits: 0 },
  { key: "pm25", label: "PM2.5", kpi: "Avg PM2.5", icon: Sparkles, digits: 0 },
  { key: "temp", label: "Temp", kpi: "Avg Temperature", icon: Thermometer, digits: 1 },
  { key: "humidity", label: "Humidity", kpi: "Avg Humidity", icon: Droplets, digits: 0 },
  { key: "voc", label: "VOC", kpi: "Worst VOC", icon: Wind, digits: 0 },
];
const STATUS = {
  healthy:        { label: "Healthy",    pill: "bg-emerald-500/15 text-emerald-700", value: "text-emerald-700", icon: CheckCircle2, fill: "#10b981", rank: 1 },
  moderate:       { label: "Moderate",   pill: "bg-amber-500/15 text-amber-700",     value: "text-amber-700",   icon: AlertTriangle, fill: "#f59e0b", rank: 2 },
  poor:           { label: "Poor",       pill: "bg-red-500/15 text-red-700",         value: "text-red-700",     icon: AlertOctagon, fill: "#ef4444", rank: 3 },
  no_data:        { label: "No data",    pill: "bg-gray-100 text-gray-600",          value: "text-gray-400",    icon: WifiOff, rank: 0 },
  not_configured: { label: "Not set up", pill: "bg-gray-100 text-gray-600",          value: "text-gray-400",    icon: AlertTriangle, rank: 0 },
};
const KPI_BORDER = { healthy: "border-t-emerald-500", moderate: "border-t-amber-500", poor: "border-t-red-500" };
const isLive = (s) => s.status !== "no_data" && s.status !== "not_configured";

function fmtAge(iso) {
  if (!iso) return "never";
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (!Number.isFinite(s)) return "—";
  if (s < 60) return `${s} s ago`;
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return `${Math.floor(s / 86400)} d ago`;
}
const fmt = (v, digits) => (v == null ? "—" : Number(v).toFixed(digits));
const statusFor = (v, mod, poor) => (v == null || mod == null ? null : v > poor ? "poor" : v > mod ? "moderate" : "healthy");

function StatusPill({ status }) {
  const t = STATUS[status] || STATUS.no_data;
  const Icon = t.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ${t.pill} text-[10px] uppercase tracking-widest font-bold whitespace-nowrap`}>
      <Icon className="w-3 h-3" /> {t.label}
    </span>
  );
}

function Kpi({ icon: Icon, label, value, unit, status, sub }) {
  return (
    <div className={`glass rounded-lg p-5 border-t-4 ${KPI_BORDER[status] || "border-t-gray-200"}`}>
      <div className="flex items-center justify-between">
        <Icon className="w-5 h-5 text-gray-500" />
        <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">{label}</span>
      </div>
      <div className="mt-3">
        <span className="text-3xl font-extrabold tracking-tight tabular-nums">{value}</span>
        {unit && <span className="text-sm text-gray-500 ml-1">{unit}</span>}
      </div>
      {sub && <div className="text-[11px] text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}

function TrendCard({ siteId, sensors, metrics }) {
  const [metric, setMetric] = useState(metrics[0]?.key || "co2");
  const withMetric = sensors.filter((s) => s.readings[metric]);
  const worst = [...withMetric].sort((a, b) => (STATUS[b.readings[metric].status]?.rank || 0) - (STATUS[a.readings[metric].status]?.rank || 0))[0];
  const [sensorId, setSensorId] = useState(null);
  const sensor = withMetric.find((s) => s.asset_id === sensorId) || worst;
  const reading = sensor?.readings[metric];
  const [points, setPoints] = useState([]);

  useEffect(() => { if (!metrics.some((m) => m.key === metric)) setMetric(metrics[0]?.key); }, [metrics, metric]);

  useEffect(() => {
    if (!siteId || !sensor?.device_id || !reading?.register) { setPoints([]); return; }
    let cancelled = false;
    const load = () => api.get(`/v1/sites/${siteId}/telemetry/parameter-history`, {
      params: { parameter: reading.register, hours: 24, device_id: sensor.device_id },
    }).then((r) => {
      if (cancelled) return;
      setPoints((r.data || []).map((p) => ({
        t: new Date(p.ingested_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }),
        v: Number.isFinite(Number(p.value)) ? +(Number(p.value) * (reading.scale || 1)).toFixed(2) : null,
      })));
    }).catch(() => !cancelled && setPoints([]));
    load();
    const i = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(i); };
  }, [siteId, sensor?.device_id, reading?.register, reading?.scale]);

  const m = METRICS.find((x) => x.key === metric);
  return (
    <div className="glass rounded-lg p-5" data-testid="iaq-trend">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <h3 className="font-semibold">{m?.label} — last 24 hours</h3>
        <div className="flex gap-2">
          <select value={metric} onChange={(e) => setMetric(e.target.value)} className="text-xs bg-gray-50 border border-gray-200 rounded px-2 py-1" aria-label="Reading">
            {metrics.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
          </select>
          <select value={sensor?.asset_id || ""} onChange={(e) => setSensorId(e.target.value)} className="text-xs bg-gray-50 border border-gray-200 rounded px-2 py-1" aria-label="Sensor">
            {withMetric.map((s) => <option key={s.asset_id} value={s.asset_id}>{s.name}</option>)}
          </select>
        </div>
      </div>
      {points.length ? (
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={points}>
            <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
            <XAxis dataKey="t" stroke="#64748b" fontSize={10} minTickGap={24} />
            <YAxis stroke="#64748b" fontSize={10} domain={["auto", "auto"]} />
            <Tooltip formatter={(v) => [`${v} ${reading?.unit || ""}`, m?.label]} />
            {reading?.poor_above != null && <ReferenceLine y={reading.poor_above} stroke="#ef4444" strokeDasharray="5 5" label={{ value: `Poor > ${reading.poor_above}`, position: "insideTopRight", fill: "#ef4444", fontSize: 10 }} />}
            {reading?.moderate_above != null && <ReferenceLine y={reading.moderate_above} stroke="#f59e0b" strokeDasharray="5 5" />}
            <Line type="monotone" dataKey="v" stroke="#0ea5e9" strokeWidth={2} dot={false} connectNulls />
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <div className="h-[240px] flex items-center justify-center text-sm text-gray-500">No readings from {sensor?.name || "this sensor"} in the last 24 hours.</div>
      )}
    </div>
  );
}

export default function IAQMonitor() {
  const { selectedSite } = useSiteContext();
  const { user } = useAuth();
  const siteId = selectedSite && selectedSite !== "all" ? selectedSite : null;
  const [sensors, setSensors] = useState(null); // null = loading
  const [error, setError] = useState(null);
  const [, tick] = useState(0);

  useEffect(() => {
    if (!siteId) { setSensors([]); return; }
    let cancelled = false;
    setSensors(null);
    const load = () => api.get("/iaq/sensors/live", { params: { site_id: siteId } })
      .then((r) => { if (!cancelled) { setSensors(r.data || []); setError(null); } })
      .catch((e) => { if (!cancelled) { setError(e.response?.data?.detail || "Couldn't load IAQ readings. Check your connection."); setSensors((s) => s || []); } });
    load();
    const i = setInterval(load, REFRESH_MS);
    const t = setInterval(() => tick((n) => n + 1), 10000); // keeps "x s ago" moving between loads
    return () => { cancelled = true; clearInterval(i); clearInterval(t); };
  }, [siteId]);

  const list = sensors || [];
  const live = list.filter(isLive);
  const offline = list.filter((s) => s.status === "no_data");
  // Columns / KPIs / trend only for readings at least one sensor is set up to report
  const metrics = METRICS.filter((m) => list.some((s) => s.readings[m.key]));

  const kpis = useMemo(() => metrics.map((m) => {
    const rs = live.map((s) => s.readings[m.key]).filter((r) => r && r.value != null);
    if (m.key === "voc") {
      const count = { healthy: 0, moderate: 0, poor: 0 };
      rs.forEach((r) => r.status && count[r.status]++);
      const worst = count.poor ? "poor" : count.moderate ? "moderate" : rs.length ? "healthy" : null;
      return { ...m, value: worst ? STATUS[worst].label : "—", status: worst,
        sub: rs.length ? `${count.healthy} good · ${count.moderate} moderate · ${count.poor} poor` : "no live readings" };
    }
    const avg = rs.length ? rs.reduce((a, r) => a + r.value, 0) / rs.length : null;
    const lim = rs[0];
    return { ...m, value: fmt(avg, m.digits), unit: lim?.unit, status: lim ? statusFor(avg, lim.moderate_above, lim.poor_above) : null,
      sub: `${rs.length} of ${list.length} sensor${list.length === 1 ? "" : "s"}${lim?.moderate_above != null ? ` · target ≤ ${lim.moderate_above}` : ""}` };
  }), [live, list, metrics]);

  const issues = useMemo(() => {
    const out = [];
    live.forEach((s) => METRICS.forEach((m) => {
      const r = s.readings[m.key];
      if (!r || (r.status !== "moderate" && r.status !== "poor")) return;
      const limit = r.status === "poor" ? r.poor_above : r.moderate_above;
      const unit = r.unit === "index" ? "" : ` ${r.unit}`; // a VOC index has no unit
      out.push({ key: `${s.asset_id}-${m.key}`, status: r.status, title: `${s.name} — ${m.label} ${fmt(r.value, m.digits)}${unit}`,
        why: `Above the ${r.status === "poor" ? "Poor" : "Moderate"} limit of ${limit}${unit}.` });
    }));
    offline.forEach((s) => out.push({ key: `${s.asset_id}-off`, status: "no_data", title: `${s.name} — no reading ${s.last_reading_at ? `for ${fmtAge(s.last_reading_at).replace(" ago", "")}` : "yet"}`,
      why: "Check the sensor's power and its RS-485 link to the gateway." }));
    return out.sort((a, b) => (STATUS[b.status].rank || 0.5) - (STATUS[a.status].rank || 0.5));
  }, [live, offline]);

  const pmBars = live.filter((s) => s.readings.pm25?.value != null)
    .map((s) => ({ zone: s.name, pm25: s.readings.pm25.value, status: s.readings.pm25.status }));
  const pmLim = live.find((s) => s.readings.pm25)?.readings.pm25;
  const showTrend = metrics.length > 0;
  const showPm = pmBars.length > 0;

  return (
    <div className="space-y-6" data-testid="iaq-monitor-page">
      {/* HEADER */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight flex items-center gap-2">
              <Wind className="w-8 h-8 text-sky-400" strokeWidth={2.5} /> IAQ Monitor
            </h1>
            {list.length > 0 && (offline.length
              ? <span className="px-2 py-0.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-[10px] uppercase tracking-widest">{offline.length} sensor{offline.length === 1 ? "" : "s"} offline</span>
              : <span className="px-2 py-0.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] uppercase tracking-widest">Live</span>)}
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Indoor air quality · {siteId ? `${list.length} sensor${list.length === 1 ? "" : "s"} at this site · refreshes every 30 s` : "pick a site"}
          </p>
        </div>
        {list.length > 0 && <span className="px-3 py-1.5 rounded-full text-[11px] bg-white text-gray-600 border border-gray-200 self-start">Limits are set per sensor in Asset Registries → IAQ Sensors</span>}
      </div>

      {error && <div className="glass rounded-lg p-3 text-sm text-red-700 border border-red-200">{error}</div>}

      {!siteId ? (
        <div className="glass rounded-lg p-10 text-center text-sm text-gray-500">Pick a site at the top to see its IAQ sensors.</div>
      ) : sensors === null ? (
        <div className="glass rounded-lg p-10 text-center text-sm text-gray-500">Loading IAQ readings…</div>
      ) : list.length === 0 ? (
        <div className="glass rounded-lg p-10 flex flex-col items-center gap-3 text-center" data-testid="iaq-empty">
          <h2 className="font-bold text-lg">No IAQ sensors at this site yet</h2>
          <p className="text-sm text-gray-500 max-w-md leading-relaxed">
            Add one under Asset Registries → IAQ Sensors: pick the gateway and slave the sensor is wired to, then which registers carry CO₂, PM2.5, temperature, humidity and VOC.
          </p>
          {user?.role === "super_admin"
            ? <Link to="/registry/iaq-sensors" className="inline-flex items-center gap-1.5 f360-gradient text-white text-sm font-semibold rounded-md px-4 py-2"><Plus className="w-4 h-4" /> Add IAQ Sensor</Link>
            : <p className="text-xs text-gray-400">Ask your platform administrator to add one.</p>}
        </div>
      ) : (
        <>
          {/* KPI ROW */}
          {kpis.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {kpis.map((k) => <Kpi key={k.key} icon={k.icon} label={k.kpi} value={k.value} unit={k.key === "voc" ? null : k.unit} status={k.status} sub={k.sub} />)}
            </div>
          )}

          {/* ZONE TABLE */}
          <div className="glass rounded-lg overflow-hidden" data-testid="iaq-zone-table">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between gap-2 flex-wrap">
              <h2 className="font-bold text-lg">Zone-wise IAQ Readings</h2>
              <span className="text-[11px] text-gray-500">Each row is one IAQ sensor · no reading for 5 min = No data</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-wider text-gray-500 bg-gray-50">
                  <tr>
                    <th className="text-left px-5 py-3">Zone</th>
                    {metrics.map((m) => <th key={m.key} className="text-right py-3 px-2">{m.label}</th>)}
                    <th className="text-center py-3 px-2">Status</th>
                    <th className="text-right px-5 py-3">Last reading</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((s) => (
                    <tr key={s.asset_id} className="border-t border-gray-200 hover:bg-gray-50" data-testid={`iaq-zone-${s.asset_id}`}>
                      <td className="px-5 py-3">
                        <div className="font-semibold">{s.name}</div>
                        <div className="text-[11px] text-gray-500">{[s.location_label, s.slave_id && `slave ${s.slave_id}`].filter(Boolean).join(" · ")}</div>
                      </td>
                      {metrics.map((m) => {
                        const r = s.readings[m.key];
                        return (
                          <td key={m.key} className={`py-3 px-2 text-right tabular-nums ${r?.status ? `font-bold ${STATUS[r.status].value}` : r?.value == null ? "text-gray-400" : ""}`}>
                            {r ? (r.value == null ? "—" : `${fmt(r.value, m.digits)}${m.key === "temp" ? " °C" : m.key === "humidity" ? " %" : ""}`) : <span className="text-gray-300">n/a</span>}
                          </td>
                        );
                      })}
                      <td className="py-3 px-2 text-center"><StatusPill status={s.status} /></td>
                      <td className="px-5 py-3 text-right text-[11px] text-gray-500 whitespace-nowrap">{fmtAge(s.last_reading_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* CHARTS */}
          {(showTrend || showPm) && (
            <div className={`grid grid-cols-1 gap-4 ${showTrend && showPm ? "lg:grid-cols-2" : ""}`}>
              {showTrend && <TrendCard siteId={siteId} sensors={list} metrics={metrics} />}
              {showPm && (
                <div className="glass rounded-lg p-5">
                  <h3 className="font-semibold mb-3">PM2.5 by zone — now</h3>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={pmBars}>
                      <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
                      <XAxis dataKey="zone" stroke="#64748b" fontSize={10} interval={0} />
                      <YAxis stroke="#64748b" fontSize={10} />
                      <Tooltip formatter={(v) => [`${v} µg/m³`, "PM2.5"]} />
                      {pmLim?.poor_above != null && <ReferenceLine y={pmLim.poor_above} stroke="#ef4444" strokeDasharray="5 5" label={{ value: `Poor > ${pmLim.poor_above}`, position: "insideTopRight", fill: "#ef4444", fontSize: 10 }} />}
                      {pmLim?.moderate_above != null && <ReferenceLine y={pmLim.moderate_above} stroke="#f59e0b" strokeDasharray="5 5" />}
                      <Bar dataKey="pm25" radius={[6, 6, 0, 0]}>
                        {pmBars.map((b) => <Cell key={b.zone} fill={STATUS[b.status]?.fill || "#0ea5e9"} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          )}

          {/* OUT OF RANGE NOW */}
          <div className="glass rounded-lg p-5" data-testid="iaq-alerts">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-400" strokeWidth={2.5} /> Out of range now</h3>
            {issues.length ? (
              <div className="space-y-2">
                {issues.map((i) => (
                  <div key={i.key} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50">
                    <StatusPill status={i.status} />
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-gray-900">{i.title}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{i.why}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-emerald-700">Every sensor is reporting and every reading is within its limits.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
